import { ClothsConnectionType, Cuts, CutTypes } from "./types";
import { Vector2, vector2 } from "flexible-vector";
import gcd from "gcd";

const minRelationNB = 0.0;
const minRelationAB = 0.9;
const maxRelationAB = 1.1;

export function getRelativePoint(
	src: ClothsConnectionType,
	dest: ClothsConnectionType,
	cut = Cuts.SYMMETRICAL,
	netLength = 0,
	topNetCount = 0,
	bottomNetCount = 0,
	firstParam = 0,
	secondParam = 0,
	verticalNetsCount = 0,
	cutSign = -1,
	cutType = CutTypes.noSet,
	u1 = 0,
	u2 = 0
): Vector2 {
	if (src === dest) {
		return vector2(0, 0);
	}

	const height = getHeight(netLength, verticalNetsCount, u2);

	if (cut === Cuts.FIXED_LEFT) {
		if (MajorConnectionType(src, dest, false)) {
			return getRelativePoint(
				dest,
				src,
				cut,
				netLength,
				topNetCount,
				bottomNetCount,
				firstParam,
				secondParam,
				verticalNetsCount,
				cutSign,
				cutType,
				u1,
				u2
			).times(-1);
		}

		const vSupRTOL = vector2(topLength(topNetCount, netLength, u1) / 2.0, 0);
		const vInfLTOR = vector2(
			-bottomLength(bottomNetCount, netLength, u1) / 2.0,
			0
		);

		const lLeftoverNet = getLeftoverNetsHz(
			cutType,
			firstParam,
			secondParam,
			verticalNetsCount
		);

		let vIzqTTOB = vector2(0, 0);

		if (verticalNetsCount === 0) {
			vIzqTTOB.x = 0;
		} else {
			vIzqTTOB.x =
				(cutSign * (lLeftoverNet * u1) * height) / (verticalNetsCount * u2);
		}
		vIzqTTOB.y = height;

		let nextPoint = vector2(0, 0);

		switch (src) {
			case ClothsConnectionType.rsup:
			case ClothsConnectionType.csup:
				nextPoint = vSupRTOL;
				break;
			case ClothsConnectionType.lsup:
				nextPoint = vIzqTTOB;
				break;
			case ClothsConnectionType.linf:
			case ClothsConnectionType.cinf:
				nextPoint = vInfLTOR;
				break;
		}
		const aux = getRelativePoint(
			nextConnectionCloths(src, false),
			dest,
			cut,
			netLength,
			topNetCount,
			bottomNetCount,
			firstParam,
			secondParam,
			verticalNetsCount,
			cutSign,
			cutType,
			u1,
			u2
		);

		return nextPoint.plus(aux);
	} else if (cut === Cuts.FIXED_RIGHT) {
		if (MajorConnectionType(src, dest, true)) {
			return getRelativePoint(
				dest,
				src,
				cut,
				netLength,
				topNetCount,
				bottomNetCount,
				firstParam,
				secondParam,
				verticalNetsCount,
				cutSign,
				cutType,
				u1,
				u2
			).times(-1);
		}

		const vSupLTOR = vector2(-topLength(topNetCount, netLength, u1) / 2.0, 0);
		const vInfRTOL = vector2(
			bottomLength(bottomNetCount, netLength, u1) / 2.0,
			0
		);

		let rLeftOverNet = getLeftoverNetsHz(
			cutType,
			firstParam,
			secondParam,
			verticalNetsCount
		);

		let vDerTTOB = vector2(0, 0);

		if (verticalNetsCount === 0) {
			vDerTTOB.x = 0;
		} else {
			vDerTTOB.x =
				(cutSign * (rLeftOverNet * u1) * height) / (verticalNetsCount * u2);
		}

		vDerTTOB.y = height;

		let nextPoint = vector2(0, 0);

		switch (src) {
			case ClothsConnectionType.lsup:
			case ClothsConnectionType.csup:
				nextPoint = vSupLTOR;
				break;
			case ClothsConnectionType.rsup:
				nextPoint = vDerTTOB;
				break;
			case ClothsConnectionType.rinf:
			case ClothsConnectionType.cinf:
				nextPoint = vInfRTOL;
				break;
		}
		const aux = getRelativePoint(
			nextConnectionCloths(src, true),
			dest,
			cut,
			netLength,
			topNetCount,
			bottomNetCount,
			firstParam,
			secondParam,
			verticalNetsCount,
			cutSign,
			cutType,
			u1,
			u2
		);

		return nextPoint.plus(aux);
	} else {
		if (MajorConnectionType(src, dest, true)) {
			return getRelativePoint(
				dest,
				src,
				cut,
				netLength,
				topNetCount,
				bottomNetCount,
				firstParam,
				secondParam,
				verticalNetsCount,
				cutSign,
				cutType,
				u1,
				u2
			).times(-1);
		}

		const vSupLTOR = vector2(-topLength(topNetCount, netLength, u1) / 2.0, 0);
		const vInfRTOL = vector2(
			bottomLength(bottomNetCount, netLength, u1) / 2.0,
			0
		);
		const vHeight = vector2(0, -height);

		let nextPoint = vector2(0, 0);

		switch (src) {
			case ClothsConnectionType.lsup:
			case ClothsConnectionType.csup:
				nextPoint = vSupLTOR;
				break;
			case ClothsConnectionType.rsup:
				nextPoint = vSupLTOR.times(-1).minus(vHeight).minus(vInfRTOL);
				break;
			case ClothsConnectionType.rinf:
			case ClothsConnectionType.cinf:
				nextPoint = vInfRTOL;
				break;
		}
		const aux = getRelativePoint(
			nextConnectionCloths(src, true),
			dest,
			cut,
			netLength,
			topNetCount,
			bottomNetCount,
			firstParam,
			secondParam,
			verticalNetsCount,
			cutSign,
			cutType,
			u1,
			u2
		);

		return nextPoint.plus(aux);
	}
}

export function topLength(topNetCount: number, netLength: number, u1: number) {
	return (topNetCount * netLength * u1) / 1000.0;
}

export function bottomLength(
	bottomNetCount: number,
	netLength: number,
	u1: number
) {
	return (bottomNetCount * netLength * u1) / 1000.0;
}

function MajorConnectionType(
	src: ClothsConnectionType,
	dest: ClothsConnectionType,
	cw: boolean
) {
	if (src === dest) {
		return false;
	} else {
		return (
			MapConnectionTypeDirection(src, cw) > MapConnectionTypeDirection(dest, cw)
		);
	}
}

function MapConnectionTypeDirection(
	clothsConnectionType: ClothsConnectionType,
	cw: boolean
) {
	if (cw) {
		return clothsConnectionType as number;
	} else {
		switch (clothsConnectionType) {
			case ClothsConnectionType.rsup:
				return 0;
			case ClothsConnectionType.csup:
				return 1;
			case ClothsConnectionType.lsup:
				return 2;
			case ClothsConnectionType.linf:
				return 3;
			case ClothsConnectionType.cinf:
				return 4;
			case ClothsConnectionType.rinf:
				return 5;
			default:
				throw new Error("El parametro no es valido.");
		}
	}
}

function getLeftoverNetsHz(
	cutType: CutTypes,
	firstCutParam: number,
	secondCutParam: number,
	netsVt: number
) {
	const verticals: number[] = [];
	const horizontals: number[] = [];

	switch (cutType) {
		case CutTypes.AB:
			verticals.push(0.5);
			horizontals.push(0.5);
			break;
		case CutTypes.N:
			verticals.push(0.5);
			horizontals.push(0.5);
			verticals.push(0.5);
			horizontals.push(-0.5);
			break;
		case CutTypes.NB:
			for (let i = 0; i < firstCutParam * 2; i++) {
				verticals.push(0.5);
				horizontals.push(i % 2 === 0 ? 0.5 : -0.5);
			}
			for (
				let i = firstCutParam * 2;
				i < firstCutParam * 2 + secondCutParam;
				i++
			) {
				verticals.push(0.5);
				horizontals.push(0.5);
			}
			break;
		case CutTypes.TB:
			for (let i = 0; i < firstCutParam * 2; i++) {
				verticals.push(i % 2 === 0 ? 0.5 : -0.5);
				horizontals.push(0.5);
			}
			for (
				let i = firstCutParam * 2;
				i < firstCutParam * 2 + secondCutParam;
				i++
			) {
				verticals.push(0.5);
				horizontals.push(0.5);
			}
			break;
	}

	let partialIndex = 0;
	let hzs = 0;
	let vts = 0;

	while (vts < netsVt) {
		vts += verticals[partialIndex];
		hzs += horizontals[partialIndex];
		partialIndex = (partialIndex + 1) % verticals.length;
	}
	return hzs;
}

function nextConnectionCloths(clothsType: ClothsConnectionType, cw: boolean) {
	if (cw) {
		switch (clothsType) {
			case ClothsConnectionType.lsup:
				return ClothsConnectionType.csup;
			case ClothsConnectionType.csup:
				return ClothsConnectionType.rsup;
			case ClothsConnectionType.rsup:
				return ClothsConnectionType.rinf;
			case ClothsConnectionType.rinf:
				return ClothsConnectionType.cinf;
			case ClothsConnectionType.cinf:
				return ClothsConnectionType.linf;
			case ClothsConnectionType.linf:
				return ClothsConnectionType.lsup;
		}
	} else {
		switch (clothsType) {
			case ClothsConnectionType.rsup:
				return ClothsConnectionType.csup;
			case ClothsConnectionType.csup:
				return ClothsConnectionType.lsup;
			case ClothsConnectionType.lsup:
				return ClothsConnectionType.linf;
			case ClothsConnectionType.linf:
				return ClothsConnectionType.cinf;
			case ClothsConnectionType.cinf:
				return ClothsConnectionType.rinf;
			case ClothsConnectionType.rinf:
				return ClothsConnectionType.rsup;
		}
	}
}

export function calculateSymmetricCut(
	topNetCount: number,
	bottomNetCount: number,
	netCountVt: number
) {
	const leftoverNets = (topNetCount - bottomNetCount) / 2;

	return getCutRelation(netCountVt, Math.abs(leftoverNets));
}

export function calculateRightCut(
	leftCut: CutTypes,
	firstParam: number,
	secondParam: number,
	netCountVt: number,
	bottomNetCount: number,
	topNetCount: number,
	sign: number
) {
	let lLeftoverNetCount = getLeftoverNetsHz(
		leftCut,
		firstParam,
		secondParam,
		netCountVt
	);

	let rLeftoverNetCount =
		bottomNetCount - sign * lLeftoverNetCount - topNetCount;

	return getCutRelation(netCountVt, Math.abs(rLeftoverNetCount));
}

export function calculateLeftCut(
	rightCut: CutTypes,
	firstParam: number,
	secondParam: number,
	netCountVt: number,
	bottomNetCount: number,
	topNetCount: number,
	sign: number
) {
	let rLeftoverNetCount = getLeftoverNetsHz(
		rightCut,
		firstParam,
		secondParam,
		netCountVt
	);
	let lLeftoverNetCount =
		bottomNetCount + sign * rLeftoverNetCount - topNetCount;

	const result = getCutRelation(netCountVt, Math.abs(lLeftoverNetCount));

	return result;
}

function getCutRelation(leftoverNetVt: number, leftoverNetHz: number) {
	let cutType = CutTypes.noSet;
	let firstParamCut = 0;
	let secondParamCut = 0;

	if (leftoverNetVt === 0) {
		return { cutType, firstParamCut, secondParamCut };
	}

	if (leftoverNetHz / leftoverNetVt <= minRelationNB) {
		cutType = CutTypes.N;
		firstParamCut = 1;
		secondParamCut = 1;

		return { cutType, firstParamCut, secondParamCut };
	}

	if (
		leftoverNetHz / leftoverNetVt <= maxRelationAB &&
		leftoverNetHz / leftoverNetVt >= minRelationAB
	) {
		cutType = CutTypes.AB;
		firstParamCut = 1;
		secondParamCut = 1;

		return { cutType, firstParamCut, secondParamCut };
	}

	const approxNetRelation = leftoverNetHz / leftoverNetVt;
	let indiceNumMenorRelacionDenomNum = 10;
	let indiceDenomMenorRelacionDenomNum = 0;

	let minDiff = 1000;

	for (let i = 10; i > 0; i--) {
		for (let j = 19; j > 0; j--) {
			let tableRel = i / j;

			if (minDiff > Math.abs(tableRel - approxNetRelation)) {
				minDiff = Math.abs(tableRel - approxNetRelation);
				indiceNumMenorRelacionDenomNum = i;
				indiceDenomMenorRelacionDenomNum = j;
			}
		}
	}
	if (approxNetRelation > 1) {
		cutType = CutTypes.TB;

		let secondParamCutAux = 2 * indiceDenomMenorRelacionDenomNum;
		let firstParamCutAux =
			indiceNumMenorRelacionDenomNum - indiceDenomMenorRelacionDenomNum;

		let gcdCut = gcd(firstParamCutAux, secondParamCutAux);

		secondParamCut = secondParamCutAux / gcdCut;
		firstParamCut = firstParamCutAux / gcdCut;

		if (firstParamCut === 0) {
			cutType = CutTypes.AB;
		}
	} else {
		cutType = CutTypes.NB;

		let secondParamCutAux = 2 * indiceNumMenorRelacionDenomNum;
		let firstParamCutAux =
			indiceDenomMenorRelacionDenomNum - indiceNumMenorRelacionDenomNum;

		let gcdCut = gcd(firstParamCutAux, secondParamCutAux);

		secondParamCut = secondParamCutAux / gcdCut;
		firstParamCut = firstParamCutAux / gcdCut;

		if (firstParamCut === 0) {
			cutType = CutTypes.AB;
		}
	}
	return { cutType, firstParamCut, secondParamCut };
}

function getHeight(netLength: number, netCountVt: number, u2: number) {
	return (netCountVt * netLength * u2) / 1000.0;
}

export function clothArea(
	topNetCount: number,
	bottomNetCount,
	netCountVt: number,
	netLength: number,
	u1: number,
	u2: number
) {
	return (
		((topNetCount + bottomNetCount) / 2.0) *
		netCountVt *
		4 *
		(netLength / 1000 / 2) *
		(netLength / 1000 / 2) *
		u1 *
		u2
	);
}

export function threadArea(
	threadDiameter: number,
	topNetCount: number,
	bottomNetCount,
	netCountVt: number,
	netLength: number,
	u2: number
) {
	return (
		netCountVt *
		(threadDiameter / 1000) *
		(topNetCount + bottomNetCount) *
		(netLength / 1000)
	);
}

export function clothWeight(
	topNetCount: number,
	bottomNetCount: number,
	netCountVt: number,
	netLength: number,
	runnage: number,
	threadDiameter: number
) {
	return (
		((((topNetCount + bottomNetCount) / 2.0) *
			netCountVt *
			4 *
			(netLength / 1000 / 2)) /
			runnage) *
		(1 + 16 * (threadDiameter / netLength)) *
		1.09
	);
}

export function concatCutTypes(
	cutType: CutTypes,
	firstParam: number,
	secondParam: number
) {
	switch (cutType) {
		case CutTypes.TB:
			return `${firstParam}T${secondParam}B`;
		case CutTypes.NB:
			return `${firstParam}N${secondParam}B`;
		case CutTypes.AB:
			return "AB";
		case CutTypes.N:
			return "N";
	}
}
